/**
 * GDPR Art.6 lawful basis auto-mapper (Atlas 3.0 §L3.2).
 *
 * Maps each PII-carrying entity to a lawful basis using a priority-ordered
 * rule chain: explicit manifest field → domain heuristic → tag heuristic →
 * no basis (gap). Returns mapping gaps for compliance officer review.
 */
import type { AtlasManifest, Entity, LegalBasis } from '@atlas-manifest/types';
import { LEGAL_BASIS_LABELS } from './compliance.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LegalBasisMapping {
  entityId: string;
  entityName: string;
  domain: string;
  basis: LegalBasis | null;
  /** Whether the basis was explicitly set in the manifest vs. inferred. */
  explicit: boolean;
  /** Confidence in the inferred basis (0–1). Null when explicit. */
  confidence: number | null;
  /** Human-readable rationale for the assignment. */
  rationale: string;
}

export interface LegalBasisReport {
  mappings: LegalBasisMapping[];
  /** Entities with PII but no legal basis — the compliance gap. */
  gaps: LegalBasisMapping[];
  /** Entities by basis, for distribution display. */
  byBasis: Record<LegalBasis, string[]>;
  /** Overall mapping coverage (0–100). */
  coveragePct: number;
}

// ─── Domain-level heuristics ──────────────────────────────────────────────────

interface DomainRule {
  domains: string[];
  basis: LegalBasis;
  confidence: number;
  rationale: string;
}

const DOMAIN_RULES: readonly DomainRule[] = [
  {
    domains: ['auth'],
    basis: 'contract',
    confidence: 0.9,
    rationale: 'Auth data is processed to perform the service contract (Art.6(1)(b)).',
  },
  {
    domains: ['billing'],
    basis: 'contract',
    confidence: 0.95,
    rationale: 'Billing data is necessary for payment obligations under the service contract.',
  },
  {
    domains: ['lending'],
    basis: 'contract',
    confidence: 0.9,
    rationale: 'Lending data is processed to execute loan agreement terms.',
  },
  {
    domains: ['connections'],
    basis: 'consent',
    confidence: 0.85,
    rationale: 'Third-party financial connections require explicit user consent (CFPB 1033).',
  },
  {
    domains: ['ai', 'analytics'],
    basis: 'legitimate_interests',
    confidence: 0.7,
    rationale:
      'AI personalization and analytics rely on legitimate interests — requires LIA documentation.',
  },
  {
    domains: ['tax', 'reports'],
    basis: 'legal_obligation',
    confidence: 0.9,
    rationale: 'Tax and regulatory reporting is a legal obligation (IRS, FinCEN).',
  },
  {
    domains: ['goals', 'budgets', 'transactions', 'accounts'],
    basis: 'contract',
    confidence: 0.85,
    rationale: 'Core personal-finance data is processed to deliver the contracted service.',
  },
  {
    domains: ['investments'],
    basis: 'contract',
    confidence: 0.85,
    rationale: 'Investment tracking is a core contracted service feature.',
  },
  {
    domains: ['family'],
    basis: 'consent',
    confidence: 0.8,
    rationale:
      'Family group data involves minors — explicit verifiable consent required per COPPA/KOSA.',
  },
  {
    domains: ['notifications', 'messaging'],
    basis: 'legitimate_interests',
    confidence: 0.7,
    rationale: 'Service communications rely on legitimate interests with opt-out.',
  },
];

// ─── Tag-based heuristics ─────────────────────────────────────────────────────

interface TagRule {
  predicate: (tag: string) => boolean;
  basis: LegalBasis;
  confidence: number;
  rationale: string;
}

const TAG_RULES: readonly TagRule[] = [
  {
    predicate: (t) => t === 'compliance:gdpr' || t === 'pii:sensitive',
    basis: 'consent',
    confidence: 0.75,
    rationale: 'Sensitive PII typically requires explicit consent under GDPR Art.9.',
  },
  {
    predicate: (t) => t === 'age-gated' || t === 'compliance:coppa' || t === 'compliance:kosa',
    basis: 'consent',
    confidence: 0.9,
    rationale: 'Age-gated content requires verifiable parental consent.',
  },
  {
    predicate: (t) => t === 'audit' || t === 'compliance:tila' || t === 'compliance:fcra',
    basis: 'legal_obligation',
    confidence: 0.85,
    rationale: 'Regulatory audit data is processed under legal obligation.',
  },
];

// ─── Infer basis ──────────────────────────────────────────────────────────────

function inferBasis(entity: Entity): {
  basis: LegalBasis | null;
  confidence: number;
  rationale: string;
} {
  // 1. Tag rules (highest priority heuristic)
  for (const rule of TAG_RULES) {
    if (entity.tags.some(rule.predicate)) {
      return { basis: rule.basis, confidence: rule.confidence, rationale: rule.rationale };
    }
  }
  // 2. Domain rules
  for (const rule of DOMAIN_RULES) {
    if (rule.domains.includes(entity.domain)) {
      return { basis: rule.basis, confidence: rule.confidence, rationale: rule.rationale };
    }
  }
  // 3. No rule matched
  return { basis: null, confidence: 0, rationale: 'No matching domain or tag rule found.' };
}

function isPiiEntity(entity: Entity): boolean {
  return (
    entity.piiCategory !== undefined ||
    entity.tags.some((t) => t.startsWith('pii:'))
  );
}

// ─── Main mapper ──────────────────────────────────────────────────────────────

export function buildLegalBasisReport(manifest: AtlasManifest): LegalBasisReport {
  const mappings: LegalBasisMapping[] = [];

  for (const entity of manifest.entities) {
    if (!isPiiEntity(entity)) continue;

    if (entity.legalBasis) {
      // Explicit manifest field — highest confidence
      mappings.push({
        entityId: entity.id,
        entityName: entity.name,
        domain: entity.domain,
        basis: entity.legalBasis,
        explicit: true,
        confidence: null,
        rationale: 'Explicitly declared in manifest.',
      });
    } else {
      const inferred = inferBasis(entity);
      mappings.push({
        entityId: entity.id,
        entityName: entity.name,
        domain: entity.domain,
        basis: inferred.basis,
        explicit: false,
        confidence: inferred.confidence,
        rationale: inferred.rationale,
      });
    }
  }

  const gaps = mappings.filter((m) => m.basis === null);

  const byBasis: Record<LegalBasis, string[]> = {
    consent: [],
    contract: [],
    legal_obligation: [],
    vital_interests: [],
    public_task: [],
    legitimate_interests: [],
  };
  for (const m of mappings) {
    if (m.basis) {
      byBasis[m.basis].push(m.entityId);
    }
  }

  const coveragePct =
    mappings.length === 0
      ? 100
      : Math.round(((mappings.length - gaps.length) / mappings.length) * 100);

  return { mappings, gaps, byBasis, coveragePct };
}

/** Look up mapping for a single entity. */
export function getEntityLegalBasis(
  manifest: AtlasManifest,
  entityId: string,
): LegalBasisMapping | null {
  const report = buildLegalBasisReport(manifest);
  return report.mappings.find((m) => m.entityId === entityId) ?? null;
}

/** Human-readable label for a basis. */
export function legalBasisLabel(basis: LegalBasis): string {
  return LEGAL_BASIS_LABELS[basis];
}
